import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Checkbox, Toolbar } from "common";
import useSettings from "features/settings/useSettings";
import { formatNumber, formatPrice } from "helpers/priceHelper";
import I18n from "i18n";
import { size } from "lodash";
import React from "react";
import { getReportValueLabels } from "../../reportValueLabels";
import { useAdvancedReportContext } from "../useAdvancedReport";

export type GroupedReportData = {
  label: string;
  amount: number;
  canceled_amount: number;
  canceled_count: number;
  closed_event_count: number;
  program_closed_count: number;
  canceled_guests: number;
  count: number;
  guests: number;
  color: string;
};

export interface AdvancedReportPivotTableProps {}

const AdvancedReportPivotTable: React.FC<
  AdvancedReportPivotTableProps
> = () => {
  const {
    pivot: data,
    params,
    excelUrl,
    excelWithBooking,
    setExcelWithBooking,
  } = useAdvancedReportContext(); // data is of type ChartData
  const [settings, setSettings] = useSettings({ key: "advancedReport" });
  function formatValue(value?: number) {
    if (
      params.value === "amount" ||
      params.value === "canceled_amount" ||
      params.value === "refund_amount" ||
      params.value === "dynamic_price_diff"
    ) {
      return formatPrice(value, settings.currency, settings.short_prices);
    }
    return formatNumber(value);
  }

  // Get labels (dates)
  const dates = data.labels as string[];

  // Datasets
  const datasets = data.datasets;

  // Compute column totals (normal and canceled)
  const columnTotals = dates.map((_, colIndex) => {
    let total = 0;
    let canceledTotal = 0;
    datasets.forEach(dataset => {
      total += dataset.data[colIndex] as number;
      //@ts-ignore
      canceledTotal += dataset.dataCanceled[colIndex] as number;
    });
    return { total, canceledTotal };
  });

  // Compute grand totals (normal and canceled)
  const grandTotal = columnTotals.reduce((sum, { total }) => sum + total, 0);
  const grandCanceledTotal = columnTotals.reduce(
    (sum, { canceledTotal }) => sum + canceledTotal,
    0,
  );
  const valueLabels = getReportValueLabels(params.value);
  let groupName = I18n.t("Program.titleSingular");
  if (params.groupBy === "boat") {
    groupName = I18n.t("Boat.titleSingular");
  } else if (params.groupBy === "partner") {
    groupName = I18n.t("Partner.titleSingular");
  } else if (params.groupBy === "partner_group") {
    groupName = I18n.t("PartnerGroup.titleSingular");
  } else if (params.groupBy === "program_variant") {
    groupName = I18n.t("ProgramVariant.titleSingular");
  }
  return (
    <>
      <TableContainer component={Paper}>
        <Table
          padding="none"
          sx={{
            "& td, th": {
              py: "3px", // General vertical padding for all cells
              px: "8px", // General horizontal padding for all cells
            },
            "& td:first-of-type, th:first-of-type": {
              pl: "16px", // Left padding for the first column cells
            },
            "& td:last-of-type, th:last-of-type": {
              pr: "16px", // Right padding for the last column cells
            },
            "& thead th": {
              py: "12px", // Top padding for the header cells
            },
            "& tr:last-of-type td": {
              py: "12px", // Bottom padding for the last row cells
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle1" fontWeight="bold">
                  {groupName}
                </Typography>
              </TableCell>
              {dates.map((date, index) => (
                <TableCell key={index} align="right">
                  <Typography variant="subtitle1" fontWeight="bold">
                    {date}
                  </Typography>
                </TableCell>
              ))}
              <TableCell align="right">
                <Typography variant="subtitle1" fontWeight="bold">
                  Total
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {datasets.map((dataset, rowIndex) => {
              const color = dataset.backgroundColor || (dataset as any).color;
              return (
                <TableRow key={rowIndex}>
                  <TableCell component="th" scope="row">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 16,
                          minWidth: 16,
                          height: 16,
                          backgroundColor: color,
                          borderRadius: "50%",
                        }}
                      />
                      {dataset.label}
                    </Box>
                  </TableCell>
                  {dataset.data.map((value, colIndex) => {
                    //@ts-ignore
                    const canceledValue = dataset.dataCanceled[colIndex];
                    return (
                      <TableCell
                        key={colIndex}
                        align="right"
                        sx={{
                          backgroundColor: color,
                          color: theme => theme.palette.getContrastText(color),
                        }}
                      >
                        <Typography sx={{ fontWeight: 500 }}>
                          {formatValue(value)}
                        </Typography>
                        {settings.show_canceled &&
                          !isNaN(canceledValue) &&
                          canceledValue !== 0 && (
                            <Typography
                              sx={{ fontSize: "0.7rem", opacity: 0.6 }}
                            >
                              ({formatValue(canceledValue)})
                            </Typography>
                          )}
                      </TableCell>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: color,
                      color: theme => theme.palette.getContrastText(color),
                      fontWeight: "bold",
                    }}
                  >
                    <Typography sx={{ fontWeight: 500 }}>
                      {formatValue(dataset.total)}
                    </Typography>
                    {settings.show_canceled &&
                      !isNaN((dataset as any).canceledTotal) &&
                      (dataset as any).canceledTotal !== 0 && (
                        <Typography sx={{ fontSize: "0.7rem", opacity: 0.6 }}>
                          ({formatValue((dataset as any).canceledTotal)})
                        </Typography>
                      )}
                  </TableCell>
                </TableRow>
              );
            })}
            {/* Add a totals row */}
            <TableRow>
              <TableCell>
                <Typography variant="subtitle1" fontWeight="bold">
                  Total
                </Typography>
              </TableCell>
              {columnTotals.map((totals, index) => (
                <TableCell
                  key={index}
                  align="right"
                  sx={{ fontWeight: "bold" }}
                >
                  <Box
                    display="flex"
                    alignItems="baseline"
                    gap={0.5}
                    justifyContent="flex-end"
                  >
                    {size(dates) === 1 && (
                      <Typography variant="caption" color="text.secondary">
                        {valueLabels.main}
                      </Typography>
                    )}
                    <Typography sx={{ fontWeight: 500 }}>
                      {formatValue(totals.total)}
                    </Typography>
                  </Box>
                  {settings.show_canceled &&
                    !isNaN(totals.canceledTotal) &&
                    totals.canceledTotal !== 0 && (
                      <Box
                        display="flex"
                        alignItems="baseline"
                        gap={0.5}
                        justifyContent="flex-end"
                      >
                        {size(dates) === 1 && (
                          <Typography variant="caption" color="text.secondary">
                            {valueLabels.canceled}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: "0.7rem", opacity: 0.6 }}>
                          ({formatValue(totals.canceledTotal)})
                        </Typography>
                      </Box>
                    )}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                <Box
                  display="flex"
                  alignItems="baseline"
                  gap={0.5}
                  justifyContent="flex-end"
                >
                  <Typography variant="caption" color="text.secondary">
                    {valueLabels.main}
                  </Typography>
                  <Typography sx={{ fontWeight: 500 }}>
                    {formatValue(grandTotal)}
                  </Typography>
                </Box>
                {settings.show_canceled &&
                  !isNaN(grandCanceledTotal) &&
                  grandCanceledTotal !== 0 && (
                    <Box
                      display="flex"
                      alignItems="baseline"
                      gap={0.5}
                      justifyContent="flex-end"
                    >
                      <Typography variant="caption" color="text.secondary">
                        {valueLabels.canceled}
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", opacity: 0.6 }}>
                        ({formatValue(grandCanceledTotal)})
                      </Typography>
                    </Box>
                  )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Toolbar
        sx={{
          mt: -1,
          mb: 2,
          justifyContent: "flex-end",
          "& label span.MuiTypography-root": {
            fontSize: "0.75rem",
          },
        }}
      >
        <Checkbox
          disabled={!params.value?.includes("amount")}
          value={settings.short_prices}
          onChange={(evt: any) => {
            setSettings({ short_prices: !settings.short_prices });
          }}
          size="small"
          label="Rövid árak"
        />
        <Checkbox
          disabled={!!params.value?.includes("canceled")}
          value={settings.show_canceled}
          onChange={(evt: any) => {
            setSettings({ show_canceled: !settings.show_canceled });
          }}
          size="small"
          label="Visszamondott értékek mutatása"
        />
        {excelUrl && (
          <Toolbar sx={{ borderRadius: 1, border: "1px solid #ddd" }}>
            <Tooltip title="Excel export">
              <IconButton href={excelUrl} download>
                <svg
                  style={{ width: 20, height: 20, fill: "currentColor" }}
                  viewBox="0 0 24 24"
                >
                  <path d="M 12 3 L 2 5 L 2 19 L 12 21 L 12 3 z M 14 5 L 14 7 L 16 7 L 16 9 L 14 9 L 14 11 L 16 11 L 16 13 L 14 13 L 14 15 L 16 15 L 16 17 L 14 17 L 14 19 L 21 19 C 21.552 19 22 18.552 22 18 L 22 6 C 22 5.448 21.552 5 21 5 L 14 5 z M 18 7 L 20 7 L 20 9 L 18 9 L 18 7 z M 4.1757812 8.296875 L 5.953125 8.296875 L 6.8769531 10.511719 C 6.9519531 10.692719 7.0084063 10.902625 7.0664062 11.140625 L 7.0917969 11.140625 C 7.1247969 10.997625 7.1919688 10.779141 7.2929688 10.494141 L 8.3222656 8.296875 L 9.9433594 8.296875 L 8.0078125 11.966797 L 10 15.703125 L 8.2714844 15.703125 L 7.1582031 13.289062 C 7.1162031 13.204062 7.0663906 13.032922 7.0253906 12.794922 L 7.0097656 12.794922 C 6.9847656 12.908922 6.934375 13.079594 6.859375 13.308594 L 5.7363281 15.703125 L 4 15.703125 L 6.0605469 11.996094 L 4.1757812 8.296875 z M 18 11 L 20 11 L 20 13 L 18 13 L 18 11 z M 18 15 L 20 15 L 20 17 L 18 17 L 18 15 z" />
                </svg>
              </IconButton>
            </Tooltip>

            <Checkbox
              value={excelWithBooking}
              onChange={(evt: any) => {
                setExcelWithBooking(!excelWithBooking);
              }}
              size="small"
              label="Foglalásokkal együtt"
            />
          </Toolbar>
        )}
      </Toolbar>
    </>
  );
};

export default AdvancedReportPivotTable;
